import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getApiUrl } from '../config/api';
import API_CONFIG from '../config/api';

// Async thunks for data fetching
export const fetchSurveyAnalytics = createAsyncThunk(
  'surveyResponses/fetchAnalytics',
  async ({surveyId, dateRange = 'all'}, { rejectWithValue }) => {
    try {
      const response = await fetch(
        getApiUrl(`${API_CONFIG.ENDPOINTS.SURVEYS.GET_ANALYTICS}?survey_id=${surveyId}&range=${dateRange}`)
      );
      const data = await response.json();
      
      if (data.status === 'success') {
        return data.data;
      } else {
        return rejectWithValue(data.message || 'Failed to fetch analytics');
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSurveyResponses = createAsyncThunk(
  'surveyResponses/fetchResponses',
  async (surveyId, { rejectWithValue }) => {
    try {
      const response = await fetch(
        getApiUrl(`${API_CONFIG.ENDPOINTS.SURVEYS.GET_RESPONSES}?survey_id=${surveyId}`)
      );
      const data = await response.json();
      
      if (data.status === 'success') {
        console.log("fetchResponses",data.data);
        return {...data.data, survey_name: data.data.survey_name || data.data.survey?.name}
      } else {
        return rejectWithValue(data.message || 'Failed to fetch responses');
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
 
  
);

export const fetchPendingResponses = createAsyncThunk(
  'surveyResponses/fetchPendingResponses',
  async (surveyId, { rejectWithValue }) => {
    try {
      // Fetch both started and pending responses
      const [startedResponse, pendingResponse] = await Promise.all([
        fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.SURVEYS.GET_RESPONSES}?survey_id=${surveyId}&status=started`)),
        fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.SURVEYS.GET_RESPONSES}?survey_id=${surveyId}&status=pending`))
      ]);

      const [startedData, pendingData] = await Promise.all([
        startedResponse.json(),
        pendingResponse.json()
      ]);

      if (startedData.status === 'success' && pendingData.status === 'success') {
        // Combine both types of responses
        const allResponses = [
          ...(startedData.data.responses || []).map(r => ({ ...r, status: 'started' })),
          ...(pendingData.data.responses || []).map(r => ({ ...r, status: 'pending' }))
        ];
        return allResponses;
      } else {
        return rejectWithValue('Failed to fetch incomplete responses');
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deletePendingResponse = createAsyncThunk(
  'surveyResponses/deletePendingResponse',
  async (responseId, { rejectWithValue }) => {
    try {
      const response = await fetch(
        getApiUrl(`${API_CONFIG.ENDPOINTS.SURVEYS.DELETE_RESPONSE}?response_id=${responseId}`),
        { method: 'DELETE' }
      );
      const data = await response.json();

      if (data.status === 'success') {
        return responseId;
      } else {
        return rejectWithValue(data.message || 'Failed to delete response');
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add new action
export const fetchSurveyDetails = createAsyncThunk(
  'surveyResponses/fetchSurveyDetails',
  async (surveyId, { rejectWithValue }) => {
    try {
      const response = await fetch(
        getApiUrl(`${API_CONFIG.ENDPOINTS.SURVEYS.GET}?id=${surveyId}`)
      );
      const data = await response.json();
      
      if (data.status === 'success') {
        return {
          survey_name: data.survey.name,
          // other survey details if needed
        };
      }
      return rejectWithValue(data.message || 'Failed to fetch survey details');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update the calculateStats function to handle different data sources
const calculateStats = (state) => {
  // Get counts from both completed responses and in-progress ones
  const completed = state.responseCounts.completed || 0;
  const started = state.responseCounts.started || 0;
  const pending = state.responseCounts.pending || 0;
  
  // Calculate totals properly
  const inProgress = started + pending;
  const totalResponses = completed + inProgress;
  
  return {
    totalResponses: totalResponses,
    completionRate: totalResponses > 0 ? Math.round((completed / totalResponses) * 100) : 0,
    completionText: `${completed} of ${totalResponses} completed`,
    pendingText: `${completed} completed • ${inProgress} in progress`,
    responseRate: state.analytics?.response_rate || 0,
    responseText: `${state.analytics?.today_responses || 0} today`,
    avgTime: state.analytics?.avg_duration_formatted || '0m 0s'
  };
};

const initialState = {
  analytics: null,
  responses: [],
  questions: [],
  pendingResponses: [],
  surveyName: null,
  responseCounts: {
    started: 0,
    pending: 0,
    completed: 0
  },
  currentSurveyId: null,
  dateRange: 'all',
  loading: {
    analytics: false,
    responses: false,
    pendingResponses: false
  },
  error: {
    analytics: null,
    responses: null,
    pendingResponses: null
  },
  stats: {
    totalResponses: 0,
    completionRate: 0,
    completionText: '0 of 0 completed',
    pendingText: '0 completed • 0 in progress',
    responseRate: 0,
    responseText: '0 today',
    avgTime: '0m 0s'
  }
};

const surveyResponsesSlice = createSlice({
  name: 'surveyResponses',
  initialState,
  reducers: {
    setCurrentSurvey(state, action) {
      state.currentSurveyId = action.payload;
    },
    setDateRange(state, action) {
      state.dateRange = action.payload;
    },
    resetErrors(state) {
      state.error = {
        analytics: null,
        responses: null,
        pendingResponses: null
      };
    }
  },
  extraReducers: (builder) => {
    // Analytics reducers
    builder
      .addCase(fetchSurveyAnalytics.pending, (state) => {
        state.loading.analytics = true;
        state.error.analytics = null;
      })
      .addCase(fetchSurveyAnalytics.fulfilled, (state, action) => {
        state.analytics = action.payload;
        state.loading.analytics = false;
        state.responseCounts.completed = action.payload.completed_responses || 0;
        
        // Recalculate stats
        state.stats = calculateStats(state);
      })
      .addCase(fetchSurveyAnalytics.rejected, (state, action) => {
        state.loading.analytics = false;
        state.error.analytics = action.payload;
      })
      
      // Responses reducers
      .addCase(fetchSurveyResponses.pending, (state) => {
        state.loading.responses = true;
        state.error.responses = null;
      })
      .addCase(fetchSurveyResponses.fulfilled, (state, action) => {
        state.responses = action.payload.responses || [];
        state.questions = action.payload.questions || [];
        state.surveyName = action.payload.survey_name;

        // Store survey_name in analytics as well
        state.analytics = {
          ...state.analytics,
          survey_name: action.payload.survey_name,
          total_responses: action.payload.total_responses,
          today_responses: action.payload.today_responses
        };
        
        // Update completed response count based on actual responses
        state.responseCounts.completed = state.responses.length;
        
        // Recalculate stats
        state.stats = calculateStats(state);
        state.loading.responses = false;
      })
      .addCase(fetchSurveyResponses.rejected, (state, action) => {
        state.loading.responses = false;
        state.error.responses = action.payload;
      })
      
      // Pending responses reducers
      .addCase(fetchPendingResponses.pending, (state) => {
        state.loading.pendingResponses = true;
        state.error.pendingResponses = null;
      })
      .addCase(fetchPendingResponses.fulfilled, (state, action) => {
        state.pendingResponses = action.payload || [];
        state.loading.pendingResponses = false;
        
        // Update response counts
        const pendingResponses = action.payload || [];
        state.responseCounts.started = pendingResponses.filter(r => r.status === 'started').length;
        state.responseCounts.pending = pendingResponses.filter(r => r.status === 'pending').length;
        
        // Recalculate stats
        state.stats = calculateStats(state);
      })
      .addCase(fetchPendingResponses.rejected, (state, action) => {
        state.loading.pendingResponses = false;
        state.error.pendingResponses = action.payload;
      })
      
      // Delete pending response
      .addCase(deletePendingResponse.fulfilled, (state, action) => {
        state.pendingResponses = state.pendingResponses.filter(
          response => response.id !== action.payload
        );
        
        // Recalculate stats
        state.stats = calculateStats(state);
      })
      
      // Add to extraReducers
      .addCase(fetchSurveyDetails.pending, (state) => {
        state.loading.survey = true;
        state.error.survey = null;
      })
      .addCase(fetchSurveyDetails.fulfilled, (state, action) => {
        state.loading.survey = false;
        state.surveyName = action.payload.survey_name;
        state.analytics = {
          ...state.analytics,
          survey_name: action.payload.survey_name
        };
      })
      .addCase(fetchSurveyDetails.rejected, (state, action) => {
        state.loading.survey = false;
        state.error.survey = action.payload;
      });
  }
});

export const { setCurrentSurvey, setDateRange, resetErrors } = surveyResponsesSlice.actions;
export default surveyResponsesSlice.reducer;